/* Zivaria homepage interactions */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!customElements.get('zv-slideshow')) {
    class ZvSlideshow extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-dot]'));
        this.index = 0;
        this.interval = Number(this.dataset.interval || 6) * 1000;
        if (!this.track || this.slides.length < 2) return;

        this.style.setProperty('--zv-interval', `${this.interval / 1000}s`);
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
        this.querySelector('[data-toggle]')?.addEventListener('click', () => this.toggleUserPause());

        this.track.addEventListener('scroll', () => {
          window.clearTimeout(this.scrollTimer);
          this.scrollTimer = window.setTimeout(() => {
            const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
            if (i !== this.index) this.setActive(i);
          }, 80);
        }, { passive: true });

        ['mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, () => this.hold(true)));
        ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => this.hold(false)));
        document.addEventListener('visibilitychange', () => this.hold(document.hidden));

        this.userPaused = reduceMotion.matches || this.dataset.autoplay !== 'true';
        this.dataset.userPaused = String(this.userPaused);
        this.setActive(0);
        this.play();
      }

      setActive(i) {
        this.index = i;
        this.slides.forEach((s, n) => {
          s.setAttribute('aria-hidden', String(n !== i));
          s.querySelectorAll('a, button').forEach((el) => (el.tabIndex = n === i ? 0 : -1));
        });
        this.dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
        this.restartTimer();
      }

      go(i, fromUser) {
        const n = (i + this.slides.length) % this.slides.length;
        this.track.scrollTo({ left: n * this.track.clientWidth, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        this.setActive(n);
        if (fromUser) this.restartTimer();
      }

      play() {
        this.dataset.playing = String(!this.userPaused);
        this.restartTimer();
      }

      restartTimer() {
        window.clearTimeout(this.timer);
        if (this.userPaused || this.held) return;
        // restart the progress animation on the active dot
        const active = this.dots[this.index]?.querySelector('span');
        if (active) {
          active.style.animation = 'none';
          void active.offsetWidth;
          active.style.animation = '';
        }
        this.timer = window.setTimeout(() => this.go(this.index + 1), this.interval);
      }

      hold(state) {
        this.held = state;
        this.dataset.paused = String(state);
        if (state) {
          window.clearTimeout(this.timer);
          this.remaining = this.interval;
        } else {
          this.restartTimer();
        }
      }

      toggleUserPause() {
        this.userPaused = !this.userPaused;
        this.dataset.userPaused = String(this.userPaused);
        const btn = this.querySelector('[data-toggle]');
        if (btn) btn.setAttribute('aria-label', this.userPaused ? btn.dataset.labelPlay : btn.dataset.labelPause);
        this.play();
      }
    }
    customElements.define('zv-slideshow', ZvSlideshow);
  }

  if (!customElements.get('zv-carousel')) {
    class ZvCarousel extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.prev = this.querySelector('[data-prev]');
        this.next = this.querySelector('[data-next]');
        this.bar = this.querySelector('[data-bar]');
        this.onScroll = this.update.bind(this);

        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(n);
            this.tabs[n].focus();
          });
        });
        this.prev?.addEventListener('click', () => this.page(-1));
        this.next?.addEventListener('click', () => this.page(1));
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.select(0);
      }

      get track() {
        const panel = this.panels.find((p) => !p.hidden) || this;
        return panel.querySelector('[data-track]');
      }

      select(i) {
        this.tabs.forEach((t, n) => {
          t.setAttribute('aria-selected', String(n === i));
          t.tabIndex = n === i ? 0 : -1;
        });
        this.panels.forEach((p, n) => (p.hidden = this.tabs.length ? n !== i : false));
        if (this.currentTrack) this.currentTrack.removeEventListener('scroll', this.onScroll);
        this.currentTrack = this.track;
        this.currentTrack?.addEventListener('scroll', this.onScroll, { passive: true });
        this.update();
      }

      page(dir) {
        const t = this.currentTrack;
        if (!t) return;
        t.scrollBy({ left: dir * t.clientWidth * 0.9, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }

      update() {
        const t = this.currentTrack;
        if (!t) return;
        const max = t.scrollWidth - t.clientWidth;
        if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
        if (this.next) this.next.disabled = t.scrollLeft >= max - 2;
        const foot = this.querySelector('.zv-carousel__foot');
        if (foot) foot.hidden = max <= 2;
        if (this.bar) {
          const w = Math.min(100, (t.clientWidth / t.scrollWidth) * 100);
          const x = max > 0 ? (t.scrollLeft / max) * ((100 - w) / w) * 100 : 0;
          this.bar.style.setProperty('--zv-bar-w', `${w}%`);
          this.bar.style.setProperty('--zv-bar-x', `${x}%`);
        }
      }
    }
    customElements.define('zv-carousel', ZvCarousel);
  }
})();
